// src/modules/tickets/pages/TicketsListPage.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  InputAdornment,
  MenuItem,
  useTheme,
  useMediaQuery,
  Skeleton,
  Stack,
} from "@mui/material";
import { Add, Search, Assignment } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

type Ticket = {
  _id: string;
  ticketNumber?: string;
  clientName?: string;
  deviceType?: string;
  brand?: string;
  model?: string;
  status?: string; // "open" | "pending" | "closed" | etc.
  createdBy?: { fullName?: string; name?: string; email?: string };
  assignedTo?: { name?: string; email?: string } | null;
  createdAt?: string;
};

export default function TicketsListPage() {
  useProtectedRoute();
  const { token, role } = useAuth();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(false);
  const [technicians, setTechnicians] = useState<Array<{ _id: string; name: string }>>([]);
  const [techFilter, setTechFilter] = useState<string>("");
  const [myId, setMyId] = useState<string>("");
  const isTechnician = (role || "").toUpperCase() === 'TECHNICIAN';

  const heroGradient = useMemo(
    () =>
      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 55%, #0f172a 100%)`,
    [theme]
  );
  const surfaceBorder = `1px solid ${theme.palette.mode === "light" ? "#e5e7eb" : theme.palette.divider}`;

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Load technicians for filter
  useEffect(() => {
    (async () => {
      if (!token) return;
      try {
        const res = await api.get('/users/technicians', { headers: { Authorization: `Bearer ${token}` } });
        setTechnicians(res.data || []);
      } catch {}
    })();
  }, [token]);

  // Load current user id for "My Tickets"
  useEffect(() => {
    (async () => {
      if (!token) return;
      try {
        const res = await api.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } });
        setMyId(String(res.data?._id || ''));
      } catch {}
    })();
  }, [token]);

  // Fetch tickets (tries server-side search; falls back to all)
  useEffect(() => {
    let mounted = true;
    const fetchTickets = async () => {
      try {
        setLoading(true);
        const params: string[] = [];
        if (debounced.length > 0) params.push(`search=${encodeURIComponent(debounced)}`);
        if (techFilter) params.push(`assignedTo=${encodeURIComponent(techFilter)}`);
        const url = `/tickets${params.length ? `?${params.join('&')}` : ''}`;
        const res = await api.get(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!mounted) return;
        setTickets((res.data as Ticket[]) || []);
      } catch {
        if (!mounted) return;
        setTickets([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchTickets();
    return () => {
      mounted = false;
    };
  }, [debounced, techFilter, token]);

  // Client-side filter as a safety net (in case backend ignores ?search)
  const visibleTickets = useMemo(() => {
    if (!debounced) return tickets;
    const q = debounced.toLowerCase();
    return tickets.filter((t) =>
      [t.ticketNumber, t.clientName, t.deviceType, t.brand, t.model]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [tickets, debounced]);

  const fmtDate = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString() : "—";
  const fmtTime = (iso?: string) =>
    iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";

  const statusChip = (status?: string) => {
    const s = (status || "").toLowerCase();
    let bg = "#6b7280"; // gray
    if (s === "open") bg = "#10b981"; // green
    else if (s === "pending") bg = "#f59e0b"; // amber
    else if (s === "closed") bg = "#ef4444"; // red
    return (
      <Chip
        size="small"
        label={status || "—"}
        sx={{ fontWeight: 700, textTransform: "capitalize", color: "#fff", bgcolor: bg }}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 1, md: 2 } }}>
      {/* HERO / HEADER */}
      <Card
        component={motion.div}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: surfaceBorder,
          background: heroGradient,
          color: "white",
        }}
      >
        <CardContent sx={{ py: { xs: 2.25, md: 2.75 } }}>
          <Grid container alignItems="center" spacing={2} justifyContent="space-between">
            <Grid item xs={12} md="auto">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    width: 44,
                    height: 44,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <Assignment />
                </Avatar>
                <Box>
                  <Typography variant={isSmDown ? "h5" : "h4"} sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                    Job Tickets
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Track jobs, statuses, devices, and assignment history.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.25,
                  flexWrap: "wrap",
                  justifyContent: { xs: "stretch", md: "flex-end" },
                  mt: { xs: 1, md: 0 },
                }}
              >
                <TextField
                  placeholder="Search by Ticket #, Client, or Device…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  fullWidth={isSmDown}
                  sx={{
                    minWidth: { xs: "100%", md: 360 },
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "rgba(255,255,255,0.92)",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "transparent" },
                      "&.Mui-focused fieldset": { borderColor: "transparent" },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  select
                  size="small"
                  value={techFilter}
                  onChange={(e)=>setTechFilter(e.target.value)}
                  sx={{ minWidth: 200 }}
                  placeholder="Filter by technician"
                >
                  <MenuItem value="">All Technicians</MenuItem>
                  {technicians.map(t => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                </TextField>
                {isTechnician && (
                  <Button variant="outlined" onClick={() => setTechFilter(myId)} disabled={!myId}>
                    My Tickets
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Add />}
                  onClick={() => navigate("/tickets/create")}
                  sx={{ fontWeight: 800, px: 2.2, boxShadow: "none", "&:hover": { boxShadow: 2 } }}
                >
                  Create Ticket
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* CONTENT CARD */}
      <Card
        component={motion.div}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.05 }}
        elevation={0}
        sx={{ borderRadius: 3, border: surfaceBorder, overflow: "hidden" }}
      >
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: { md: 560 }, overflow: "auto" }}>
          <Table stickyHeader size="medium" aria-label="tickets-table">
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "action.hover",
                    fontWeight: 800,
                  },
                }}
              >
                <TableCell>Ticket #</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Technician</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell colSpan={10} sx={{ py: 1.25 }}>
                      <Skeleton height={28} />
                    </TableCell>
                  </TableRow>
                ))
              ) : visibleTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Box
                      sx={{
                        py: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.mode === "light" ? "#eef2ff" : "primary.dark",
                          color: "primary.main",
                          width: 56,
                          height: 56,
                        }}
                      >
                        <Assignment />
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 0.5 }}>
                        No tickets found
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Try a different search or create a new ticket.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate("/tickets/create")}
                        sx={{ fontWeight: 800 }}
                      >
                        Create Ticket
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                visibleTickets.map((t) => (
                  <TableRow
                    key={t._id}
                    hover
                    sx={{
                      "&:hover": {
                        background:
                          theme.palette.mode === "light"
                            ? "linear-gradient(0deg, rgba(37,99,235,0.05), rgba(37,99,235,0.05))"
                            : "rgba(37,99,235,0.08)",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 700, cursor: "pointer" }}
                        onClick={() => navigate(`/tickets/${t._id}`)}
                        title="View ticket"
                      >
                        {t.ticketNumber || t._id}
                      </Typography>
                    </TableCell>
                    <TableCell>{t.clientName || <Typography color="text.disabled">—</Typography>}</TableCell>
                    <TableCell>{t.deviceType || <Typography color="text.disabled">—</Typography>}</TableCell>
                    <TableCell>{t.brand || <Typography color="text.disabled">—</Typography>}</TableCell>
                    <TableCell>{t.model || <Typography color="text.disabled">—</Typography>}</TableCell>
                    <TableCell>{statusChip(t.status)}</TableCell>
                    <TableCell>
                      {t.assignedTo?.name || t.assignedTo?.email || (
                        <Typography color="text.disabled">Unassigned</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {t.createdBy?.fullName ||
                        t.createdBy?.name ||
                        t.createdBy?.email || <Typography color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell>{fmtDate(t.createdAt)}</TableCell>
                    <TableCell>{fmtTime(t.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" variant="text" onClick={() => navigate(`/tickets/${t._id}`)} sx={{ fontWeight: 800 }}>
                          View
                        </Button>
                        <Button
                          size="small"
                          color="secondary"
                          variant="text"
                          onClick={() => navigate(`/tickets/${t._id}/edit`)}
                          sx={{ fontWeight: 800 }}
                        >
                          Edit
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Space before fixed footer seam */}
      <Box sx={{ height: 12 }} />
    </Box>
  );
}

